import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fallbackImages = [
  'https://images.pexels.com/photos/3735218/pexels-photo-3735218.jpeg',
  'https://images.pexels.com/photos/343457/pexels-photo-343457.jpeg',
  'https://images.pexels.com/photos/459411/pexels-photo-459411.jpeg',
];

const NewsSection = () => {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const res = await fetch(`${API_URL}/api/blog/posts`);
        const data = await res.json();
        setPosts(data.slice(0, 3));
      } catch (err) {
        console.error('Failed to fetch news:', err);
      }
    };
    fetchPosts();
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long', day: 'numeric', year: 'numeric'
    });
  };

  const getImage = (post, index) => post.image_url || fallbackImages[index % fallbackImages.length];

  if (posts.length === 0) return null;

  return (
    <section className="py-20 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div>
            <p className="text-[#4fd1c5] text-sm font-semibold tracking-[0.15em] uppercase mb-3">News</p>
            <h2 className="text-3xl lg:text-4xl font-light text-[#0a1628]">
              Latest <span className="font-semibold">Updates</span>
            </h2>
          </div>
          <Link to="/insights" className="inline-flex items-center gap-2 text-[#0a1628] font-semibold hover:text-[#4fd1c5] transition-colors duration-500 ease-out"
            data-testid="news-view-all">
            View All News
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>

        {/* News Grid */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Featured */}
          <div className="lg:col-span-2">
            {posts[0]?.source === 'rss' && posts[0]?.source_url ? (
              <a href={posts[0].source_url} target="_blank" rel="noopener noreferrer" className="group block">
                <div className="aspect-[16/9] overflow-hidden mb-6">
                  <img src={getImage(posts[0], 0)} alt={posts[0]?.title}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700 ease-out" />
                </div>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-[#4fd1c5] text-xs font-semibold uppercase tracking-wider">{posts[0]?.category?.split(':')[0]}</span>
                  <span className="text-gray-400 text-sm">{formatDate(posts[0]?.date)}</span>
                </div>
                <h3 className="text-xl lg:text-2xl font-semibold text-[#0a1628] group-hover:text-[#4fd1c5] transition-colors duration-500 ease-out mb-3">
                  {posts[0]?.title}
                </h3>
                <p className="text-gray-500">{posts[0]?.excerpt}</p>
              </a>
            ) : (
              <Link to={`/insights/${posts[0]?.id}`} className="group block">
                <div className="aspect-[16/9] overflow-hidden mb-6">
                  <img src={getImage(posts[0], 0)} alt={posts[0]?.title}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700 ease-out" />
                </div>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-[#4fd1c5] text-xs font-semibold uppercase tracking-wider">{posts[0]?.category?.split(':')[0]}</span>
                  <span className="text-gray-400 text-sm">{formatDate(posts[0]?.date)}</span>
                </div>
                <h3 className="text-xl lg:text-2xl font-semibold text-[#0a1628] group-hover:text-[#4fd1c5] transition-colors duration-500 ease-out mb-3">
                  {posts[0]?.title}
                </h3>
                <p className="text-gray-500">{posts[0]?.excerpt}</p>
              </Link>
            )}
          </div>

          {/* Side */}
          <div className="space-y-8">
            {posts.slice(1, 3).map((news, i) => (
              <Link key={news.id} to={`/insights/${news.id}`} className="group block">
                <div className="aspect-[16/9] overflow-hidden mb-4">
                  <img src={getImage(news, i + 1)} alt={news.title}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700 ease-out" />
                </div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-[#4fd1c5] text-xs font-semibold uppercase tracking-wider">{news.category?.split(':')[0]}</span>
                  <span className="text-gray-400 text-xs">{formatDate(news.date)}</span>
                </div>
                <h3 className="font-semibold text-[#0a1628] group-hover:text-[#4fd1c5] transition-colors duration-500 ease-out">
                  {news.title}
                </h3>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewsSection;
